import React, { useState, useEffect } from 'react';
import GraphVisualization from './components/GraphVisualization';
import ChatPanel from './components/ChatPanel';
import MementoModal from './components/MementoModal';
import HelpLegend from './components/HelpLegend';
import EmptyState from './components/EmptyState';
import WelcomeOverlay from './components/WelcomeOverlay';
import { generateSampleData } from './utils/sampleData';
import { OpenInferLogo } from './components/icons';

function App() {
  const [graphData, setGraphData] = useState(null);
  const [filteredGraph, setFilteredGraph] = useState(null);
  const [selectedMemento, setSelectedMemento] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showWelcome, setShowWelcome] = useState(true);

  useEffect(() => {
    // Generate sample data on component mount
    const sampleData = generateSampleData();
    setGraphData(sampleData);
    setFilteredGraph(sampleData);
  }, []);

  const handleSearch = (message) => {
    if (!message.trim() || !graphData) return;
    
    // Simple text search through node labels and content
    const filteredNodes = graphData.nodes.filter(node => {
      const searchText = message.toLowerCase();
      return (
        node.label.toLowerCase().includes(searchText) ||
        node.content.toLowerCase().includes(searchText) ||
        (node.category && node.category.toLowerCase().includes(searchText)) ||
        (node.topic && node.topic.toLowerCase().includes(searchText)) ||
        (node.subtopic && node.subtopic.toLowerCase().includes(searchText)) ||
        (node.subcategory && node.subcategory.toLowerCase().includes(searchText))
      );
    });
    
    // Also include edges that connect the filtered nodes
    const filteredNodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredEdges = graphData.edges.filter(edge =>
      filteredNodeIds.has(edge.source) && filteredNodeIds.has(edge.target)
    );
    
    const filtered = {
      nodes: filteredNodes,
      edges: filteredEdges
    };
    
    setFilteredGraph(filtered);
    setSearchQuery(message);
  };

  const handleNodeClick = (nodeId) => {
    const memento = graphData.nodes.find(node => node.id === nodeId);
    if (memento) {
      setSelectedMemento(memento);
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedMemento(null);
  };

  const handleMementoUpdate = (updatedMemento) => {
    if (!graphData) return;
    
    const updatedNodes = graphData.nodes.map(node => 
      node.id === updatedMemento.id ? updatedMemento : node
    );
    
    const updatedGraph = { ...graphData, nodes: updatedNodes };
    setGraphData(updatedGraph);
    
    // Update filtered graph if it contains the updated node
    if (filteredGraph) {
      const updatedFilteredNodes = filteredGraph.nodes.map(node => 
        node.id === updatedMemento.id ? updatedMemento : node
      );
      setFilteredGraph({ ...filteredGraph, nodes: updatedFilteredNodes });
    }
    
    setIsModalOpen(false);
    setSelectedMemento(null);
  };

  const handleMementoDelete = (mementoId) => {
    if (!graphData) return;
    
    const updatedNodes = graphData.nodes.filter(node => node.id !== mementoId);
    const updatedGraph = { ...graphData, nodes: updatedNodes };
    setGraphData(updatedGraph);
    
    // Update filtered graph if it contains the deleted node
    if (filteredGraph) {
      const updatedFilteredNodes = filteredGraph.nodes.filter(node => node.id !== mementoId);
      setFilteredGraph({ ...filteredGraph, nodes: updatedFilteredNodes });
    }
    
    setIsModalOpen(false);
    setSelectedMemento(null);
  };

  const clearFilter = () => {
    setFilteredGraph(graphData);
    setSearchQuery('');
  };

  const handleWelcomeClose = () => {
    setShowWelcome(false);
  };

  if (!graphData) {
    return <div className="flex justify-center items-center h-screen text-2xl text-gray-800">Loading...</div>;
  }

  // Check if we have any nodes to display
  const hasNodes = filteredGraph && filteredGraph.nodes && filteredGraph.nodes.length > 0;
  const isSearchResult = filteredGraph !== graphData;
  const isEmptySearch = isSearchResult && !hasNodes;
  const isEmptyGraph = !isSearchResult && !hasNodes;

  return (
    <div className="overflow-hidden relative w-screen h-screen">
      {/* Header with OpenInfer Logo */}
      <div className="absolute top-4 left-4 z-50">
        <OpenInferLogo 
          className="text-black" 
          color="black"
          size={48}
        />
      </div>

      {/* Help Legend */}
      <HelpLegend />
      
      {/* Graph Visualization - always render for layout but may be hidden */}
      <GraphVisualization 
        graphData={filteredGraph} 
        onNodeClick={handleNodeClick}
      />

      {/* Empty State Overlays */}
      {isEmptySearch && (
        <EmptyState 
          type="search"
          searchQuery={searchQuery}
          onClearFilter={clearFilter}
        />
      )}

      {isEmptyGraph && (
        <EmptyState 
          type="empty"
        />
      )}
      
      <ChatPanel 
        onSubmit={handleSearch}
        searchQuery={searchQuery}
        onClearFilter={clearFilter}
        isFiltered={filteredGraph !== graphData}
      />
      
      {isModalOpen && selectedMemento && (
        <MementoModal
          memento={selectedMemento}
          onClose={handleModalClose}
          onUpdate={handleMementoUpdate}
          onDelete={handleMementoDelete}
        />
      )}

      {/* Welcome Overlay */}
      {showWelcome && (
        <WelcomeOverlay onClose={handleWelcomeClose} />
      )}
    </div>
  );
}

export default App;
